import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Grid, Box, Typography, TextField, Button, Alert, Card, CardContent,
    InputAdornment, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Controls from '../../components/controls/Controls';
import { fetchData } from '../../services/ApiServices';
import { urlAccountants, urlUsers, urlBase } from '../../config';
import { isEmpty } from 'lodash';
import { logActionType, roleTranslations } from '../../services/MyCollections';
import { logActivity } from '../../services/ApiServices';
import * as MyCollections from '../../services/MyCollections';

export default function UserManagement({ token, userName, onNotify, darkTheme }) {
    const [subTab, setSubTab] = useState('list'); // 'list' | 'editRole' | 'addUser'
    const [formData, setFormData] = useState({});
    const [accountants, setAccountants] = useState([]);
    const [users, setUsers] = useState([]);
    const [role, setRole] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [createdCredentials, setCreatedCredentials] = useState(null);
    const [copied, setCopied] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(null);
    const [copiedNewPassword, setCopiedNewPassword] = useState(false);
    const [reset2FaTarget, setReset2FaTarget] = useState(null);
    const [reset2FaConfirmOpen, setReset2FaConfirmOpen] = useState(false);
    const gridRef = useRef();

    const refreshUsers = async () => {
        try {
            const resp = await fetch(urlUsers + '/allWithRoles', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (resp.ok) {
                const data = await resp.json();
                const mapped = data.map(item => ({
                    ...item,
                    key: item.id,
                    value: (item.lastName || '') + ' ' + (item.firstName || ''),
                    role: item.userRole || item.role,
                    userRole: item.userRole || item.role
                }));
                const sorted = [...mapped].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'pl', { sensitivity: 'base' }));
                setUsers(sorted);
                return;
            }
        } catch (err) {
            // endpoint /allWithRoles jeszcze nie jest aktywny przed restartem backendu
        }

        fetchData(urlUsers + '/allForSelect', mapToSelect2, async (data) => {
            let fullList = Array.isArray(data) ? data : [];
            const missingDetails = fullList.some(u => !u.role && !u.userRole);

            if (missingDetails && fullList.length > 0) {
                try {
                    const detailedList = await Promise.all(fullList.map(async (u) => {
                        try {
                            const userResp = await fetch(urlUsers + '/' + u.id, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (userResp.ok) {
                                const detail = await userResp.json();
                                return {
                                    ...u,
                                    username: detail.username || u.username,
                                    role: detail.userRole || u.role,
                                    userRole: detail.userRole || u.role
                                };
                            }
                        } catch (e) {
                            console.error('Błąd pobierania danych użytkownika:', e);
                        }
                        return u;
                    }));
                    fullList = detailedList;
                } catch (e) {
                    console.error(e);
                }
            }

            const sorted = [...fullList].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'pl', { sensitivity: 'base' }));
            setUsers(sorted);
        }, console.error, token);
    };

    useEffect(() => {
        fetchData(urlAccountants + '/allForSelect', mapToSelect, (data) => {
            const sorted = Array.isArray(data)
                ? [...data].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'pl', { sensitivity: 'base' }))
                : [];
            setAccountants(sorted);
        }, console.error, token);
        refreshUsers();
    }, []);

    const mapToSelect = (item) => ({
        key: item.id,
        value: item.surname + ' ' + item.name,
    });

    const mapToSelect2 = (item) => ({
        ...item,
        key: item.id,
        value: item.lastName + ' ' + item.firstName,
    });

    const fetchDataLocal = useCallback((url, setter, errorHandler) => {
        fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(resp => resp.json())
            .then(data => setter(data?.userRole || data))
            .catch(error => {
                if (typeof errorHandler === 'function') errorHandler(error);
            });
    }, [token]);

    const handleEditUserRoleFromList = useCallback((userId) => {
        setSelectedUser(userId);
        fetchDataLocal(urlUsers + "/" + userId, setRole, console.error);
        setSubTab('editRole');
    }, [fetchDataLocal]);

    const handleChangePasswordFromList = useCallback((userId) => {
        setSelectedUser(userId);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordChangeSuccess(null);
        setErrors({});
        setSubTab('changePassword');
    }, []);

    const handleConfirmReset2Fa = useCallback((userId, displayName) => {
        setReset2FaTarget({ id: userId, name: displayName });
        setReset2FaConfirmOpen(true);
    }, []);

    const handleExecuteReset2Fa = async () => {
        if (!reset2FaTarget?.id) return;
        try {
            const resp = await fetch(`${urlBase}/api/auth/2fa/admin/reset/${reset2FaTarget.id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (resp.ok) {
                if (onNotify) onNotify(`Weryfikacja 2FA dla użytkownika ${reset2FaTarget.name} została zresetowana!`, 'success');
                logActivity(`Reset 2FA użytkownika: ${reset2FaTarget.name}`, userName, token);
                refreshUsers();
            } else {
                const errText = await resp.text();
                if (onNotify) onNotify('Błąd resetowania 2FA: ' + errText, 'error');
            }
        } catch (err) {
            if (onNotify) onNotify('Błąd połączenia z serwerem.', 'error');
        } finally {
            setReset2FaConfirmOpen(false);
            setReset2FaTarget(null);
        }
    };

    const getRoleBadgeColor = useCallback((userRole) => {
        switch (userRole) {
            case 'ADMIN':
            case 'ADMIN_SUPERVISOR':
                return {
                    bg: darkTheme ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2',
                    text: darkTheme ? '#FCA5A5' : '#DC2626',
                    border: darkTheme ? 'rgba(239, 68, 68, 0.3)' : '#FCA5A5'
                };
            case 'MANAGER':
            case 'SUPERVISOR':
                return {
                    bg: darkTheme ? 'rgba(59, 130, 246, 0.15)' : '#DBEAFE',
                    text: darkTheme ? '#93C5FD' : '#2563EB',
                    border: darkTheme ? 'rgba(59, 130, 246, 0.3)' : '#93C5FD'
                };
            case 'ACCOUNTANT':
            case 'CHIEF_ACCOUNTANT':
            case 'SUPERVISOR_ACCOUNTANT':
                return {
                    bg: darkTheme ? 'rgba(16, 185, 129, 0.15)' : '#D1FAE5',
                    text: darkTheme ? '#86EFAC' : '#059669',
                    border: darkTheme ? 'rgba(16, 185, 129, 0.3)' : '#6EE7B7'
                };
            case 'ACCOUNT_MANAGER':
            case 'TRADER':
                return {
                    bg: darkTheme ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7',
                    text: darkTheme ? '#FDE68A' : '#D97706',
                    border: darkTheme ? 'rgba(245, 158, 11, 0.3)' : '#FCD34D'
                };
            case 'EXTERNAL_IT':
                return {
                    bg: darkTheme ? 'rgba(168, 85, 247, 0.15)' : '#F3E8FF',
                    text: darkTheme ? '#D8B4FE' : '#9333EA',
                    border: darkTheme ? 'rgba(168, 85, 247, 0.3)' : '#D8B4FE'
                };
            default:
                return {
                    bg: darkTheme ? 'rgba(148, 163, 184, 0.15)' : '#F1F5F9',
                    text: darkTheme ? '#CBD5E1' : '#64748B',
                    border: darkTheme ? 'rgba(148, 163, 184, 0.3)' : '#CBD5E1'
                };
        }
    }, [darkTheme]);

    const columnDefs = useMemo(() => [

        {
            headerName: 'Użytkownik',
            field: 'value',
            flex: 2,
            minWidth: 180,
            sortable: true,
            filter: true,
            getQuickFilterText: (params) => params.data?.value || '',
            cellStyle: { display: 'flex', alignItems: 'center' },
            cellRenderer: (params) => {
                if (!params.data) return null;
                const initial = (params.data.lastName?.[0] || params.data.value?.[0] || 'U').toUpperCase();
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: darkTheme ? 'rgba(99, 102, 241, 0.2)' : '#EEF2FF',
                                color: darkTheme ? '#A5B4FC' : '#4F46E5',
                                fontWeight: 700,
                                fontSize: '11.5px',
                                flexShrink: 0
                            }}
                        >
                            {initial}
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: darkTheme ? '#F8FAFC' : '#1E293B' }}>
                            {params.value}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            headerName: 'Login',
            field: 'username',
            flex: 1.2,
            minWidth: 130,
            sortable: true,
            filter: true,
            cellStyle: { display: 'flex', alignItems: 'center', fontFamily: 'monospace', fontSize: '13px', color: darkTheme ? '#CBD5E1' : '#475569' },
            valueFormatter: (params) => params.value || '—',
            getQuickFilterText: (params) => params.data?.username || ''
        },
        {
            headerName: 'Przypisana rola',
            flex: 1.5,
            minWidth: 170,
            sortable: true,
            filter: true,
            cellStyle: { display: 'flex', alignItems: 'center' },
            valueGetter: (params) => {
                const currentRole = params.data?.role || params.data?.userRole;
                return roleTranslations[currentRole] || currentRole || 'Brak roli';
            },
            getQuickFilterText: (params) => {
                const r = params.data?.role || params.data?.userRole;
                return (roleTranslations[r] || r || '') + ' ' + (r || '');
            },
            cellRenderer: (params) => {
                if (!params.data) return null;
                const currentRole = params.data?.role || params.data?.userRole;
                const badge = getRoleBadgeColor(currentRole);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Chip
                            label={params.value}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                fontSize: '11.5px',
                                backgroundColor: badge.bg,
                                color: badge.text,
                                border: `1px solid ${badge.border}`
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            headerName: '2FA',
            field: 'twoFactorEnabled',
            width: 125,
            minWidth: 110,
            maxWidth: 140,
            sortable: true,
            filter: true,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
            cellRenderer: (params) => {
                if (!params.data) return null;
                const is2Fa = Boolean(params.data.twoFactorEnabled);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Chip
                            label={is2Fa ? 'Aktywne' : 'Wyłączone'}
                            size="small"
                            color={is2Fa ? 'success' : 'default'}
                            variant={is2Fa ? 'filled' : 'outlined'}
                            sx={{
                                fontWeight: 600,
                                fontSize: '11px',
                                height: '22px'
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            headerName: 'Akcje',
            width: 330,
            minWidth: 350,
            maxWidth: 400,
            sortable: false,
            filter: false,
            resizable: false,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
            cellRenderer: (params) => {
                if (!params.data) return null;
                const userId = params.data.id || params.data.key;
                return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEditUserRoleFromList(userId)}
                            sx={{
                                py: 0.2,
                                px: 1,
                                borderRadius: 1.5,
                                textTransform: 'none',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                color: darkTheme ? '#93C5FD' : '#2563EB',
                                borderColor: darkTheme ? 'rgba(59, 130, 246, 0.3)' : '#BFDBFE',
                                '&:hover': {
                                    borderColor: darkTheme ? '#60A5FA' : '#3B82F6',
                                    backgroundColor: darkTheme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(37, 99, 235, 0.05)'
                                }
                            }}
                            startIcon={<i className="fas fa-user-edit" style={{ fontSize: 10 }}></i>}
                        >
                            Zmień rolę
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleChangePasswordFromList(userId)}
                            sx={{
                                py: 0.2,
                                px: 1,
                                borderRadius: 1.5,
                                textTransform: 'none',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                color: darkTheme ? '#FCD34D' : '#D97706',
                                borderColor: darkTheme ? 'rgba(245, 158, 11, 0.3)' : '#FDE68A',
                                '&:hover': {
                                    borderColor: darkTheme ? '#FBBF24' : '#B45309',
                                    backgroundColor: darkTheme ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)'
                                }
                            }}
                            startIcon={<i className="fas fa-key" style={{ fontSize: 10 }}></i>}
                        >
                            Zmień hasło
                        </Button>
                        {params.data.twoFactorEnabled && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleConfirmReset2Fa(userId, params.data.value || params.data.username)}
                                sx={{
                                    py: 0.2,
                                    px: 1,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '11.5px',
                                    fontWeight: 600
                                }}
                                startIcon={<i className="fas fa-shield-alt" style={{ fontSize: 10 }}></i>}
                            >
                                Reset 2FA
                            </Button>
                        )}
                    </Box>
                );
            }
        }
    ], [darkTheme, handleEditUserRoleFromList, handleChangePasswordFromList, handleConfirmReset2Fa, getRoleBadgeColor]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        sortable: true,
        filter: true,
        flex: 1
    }), []);

    const onGridReady = useCallback((params) => {
        gridRef.current = params;
        if (searchTerm) {
            params.api.setQuickFilter(searchTerm);
        }
        params.api.sizeColumnsToFit();
    }, [searchTerm]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (gridRef.current?.api) {
            gridRef.current.api.setQuickFilter(val);
        }
    };

    const localOnChange = (e) => {
        const { name, value } = e.target;
        validateField(name, value);

        if (name === 'userRole' && value !== 'ACCOUNTANT') {
            setFormData(prev => ({ ...prev, [name]: value, contactId: null }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUserChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedUser(selectedValue);
        if (selectedValue) {
            fetchDataLocal(urlUsers + "/" + selectedValue, setRole, console.error);
        } else {
            setRole('');
        }
    };

    const handleRoleChange = (event) => {
        setRole(event.target.value);
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };
        if (value === null || value === '' || value === undefined) {
            newErrors[name] = 'Pole nie może być puste.';
        } else {
            delete newErrors[name];
        }
        setErrors(newErrors);
    };

    const validateFormOnSubmit = (isEditRole) => {
        let formIsValid = true;
        let validationErrors = {};

        if (!isEditRole) {
            if (isEmpty(formData.firstName)) {
                validationErrors.firstName = 'Imię jest wymagane';
                formIsValid = false;
            }
            if (isEmpty(formData.lastName)) {
                validationErrors.lastName = 'Nazwisko jest wymagane';
                formIsValid = false;
            }
            if (isEmpty(formData.username)) {
                validationErrors.username = 'Nazwa użytkownika jest wymagana';
                formIsValid = false;
            }
            if (isEmpty(formData.password)) {
                validationErrors.password = 'Hasło jest wymagane';
                formIsValid = false;
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/.test(formData.password)) {
                validationErrors.password = 'Hasło musi mieć min. 12 znaków, w tym dużą i małą literę, cyfrę oraz znak specjalny.';
                formIsValid = false;
            }
            if (isEmpty(formData.userRole)) {
                validationErrors.userRole = 'Rola jest wymagana';
                formIsValid = false;
            }
            if (formData.userRole === 'ACCOUNTANT' && !formData.contactId) {
                validationErrors.contactId = 'Przypisanie do profilu księgowego jest wymagane';
                formIsValid = false;
            }
        } else {
            if (!role) {
                validationErrors.role = 'Wybór roli jest wymagany';
                formIsValid = false;
            }
            if (!selectedUser) {
                validationErrors.id = 'Wybór użytkownika jest wymagany';
                formIsValid = false;
            }
        }

        setErrors(validationErrors);
        return formIsValid;
    };

    const handleAddUserSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateFormOnSubmit(false)) return;

        try {
            const resp = await fetch(urlUsers + "/register", {
                method: "POST",
                body: JSON.stringify(formData),
                headers: {
                    'content-type': "application/json",
                    'Authorization': `Bearer ${token}`
                }
            });

            if (resp.status === 200) {
                logActivity(logActionType.ADD_NEW_USER + formData.username, userName, token);
                setCreatedCredentials({
                    username: formData.username,
                    password: formData.password,
                    role: formData.userRole
                });
                setCopied(false);
                setFormData({});
                refreshUsers();
                if (onNotify) {
                    onNotify('Nowy użytkownik został pomyślnie utworzony!', 'success');
                }
            } else if (resp.status === 400) {
                if (onNotify) {
                    onNotify('Użytkownik o podanej nazwie już istnieje w bazie.', 'warning');
                }
            } else {
                throw new Error(`HTTP error! Status: ${resp.status}`);
            }
        } catch (error) {
            console.error('Błąd podczas tworzenia użytkownika:', error);
            if (onNotify) {
                onNotify('Wystąpił błąd podczas dodawania użytkownika.', 'error');
            }
        }
    };

    const handleSubmitRoleChange = async (e) => {
        if (e) e.preventDefault();
        if (!validateFormOnSubmit(true)) return;

        try {
            const response = await fetch(urlUsers + `/${selectedUser}/updateRole/${encodeURIComponent(role)}`, {
                method: 'PUT',
                headers: {
                    'content-type': "application/json",
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const userObj = users.find(u => u.key === selectedUser);
                logActivity(logActionType.EDIT_USER_ROLE + (userObj ? userObj.value : selectedUser), userName, token);
                refreshUsers();
                if (onNotify) {
                    onNotify('Uprawnienia użytkownika zostały pomyślnie zaktualizowane!', 'success');
                }
            } else {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
        } catch (error) {
            console.error('Błąd podczas aktualizacji uprawnień:', error);
            if (onNotify) {
                onNotify('Wystąpił błąd podczas aktualizacji uprawnień.', 'error');
            }
        }
    };

    const getRandomPassword = () => {
        const uppers = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
        const lowers = 'abcdefghijkmnopqrstuvwxyz';
        const digits = '23456789';
        const specials = '!@#$%^&*()_+';
        const all = uppers + lowers + digits + specials;
        let pwd = [
            uppers[Math.floor(Math.random() * uppers.length)],
            lowers[Math.floor(Math.random() * lowers.length)],
            digits[Math.floor(Math.random() * digits.length)],
            specials[Math.floor(Math.random() * specials.length)]
        ];
        for (let i = 0; i < 10; i++) {
            pwd.push(all[Math.floor(Math.random() * all.length)]);
        }
        return pwd.sort(() => 0.5 - Math.random()).join('');
    };

    const generateRandomPassword = () => {
        const generated = getRandomPassword();
        setNewPassword(generated);
        setConfirmPassword(generated);
        setShowNewPassword(true);
        setErrors(prev => {
            const next = { ...prev };
            delete next.newPassword;
            delete next.confirmPassword;
            return next;
        });
    };

    const generateRandomPasswordForNewUser = () => {
        const generated = getRandomPassword();
        setFormData(prev => ({ ...prev, password: generated }));
        setShowPassword(true);
        setErrors(prev => {
            const next = { ...prev };
            delete next.password;
            return next;
        });
    };

    const validatePasswordChange = () => {
        let isValid = true;
        let newErrors = {};

        if (!selectedUser) {
            newErrors.passwordUser = 'Wybór użytkownika jest wymagany';
            isValid = false;
        }
        if (isEmpty(newPassword)) {
            newErrors.newPassword = 'Nowe hasło jest wymagane';
            isValid = false;
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/.test(newPassword)) {
            newErrors.newPassword = 'Hasło musi mieć min. 12 znaków, w tym dużą i małą literę, cyfrę oraz znak specjalny.';
            isValid = false;
        }
        if (isEmpty(confirmPassword)) {
            newErrors.confirmPassword = 'Potwierdzenie hasła jest wymagane';
            isValid = false;
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Hasła nie są identyczne';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmitPasswordChange = async (e) => {
        if (e) e.preventDefault();
        if (!validatePasswordChange()) return;

        try {
            const response = await fetch(urlUsers + `/${selectedUser}/updatePassword`, {
                method: 'PUT',
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ password: newPassword })
            });

            if (response.ok) {
                const targetUser = users.find(u => (u.id || u.key) === selectedUser);
                const userDisplay = targetUser?.value || targetUser?.username || '';
                const userLogin = targetUser?.username || '';
                logActivity("Zmiana hasła użytkownika: " + (userLogin || userDisplay), userName, token);
                setPasswordChangeSuccess({
                    displayName: userDisplay,
                    username: userLogin,
                    password: newPassword
                });
                setCopiedNewPassword(false);
                setNewPassword('');
                setConfirmPassword('');
                if (onNotify) {
                    onNotify(`Hasło dla użytkownika ${userDisplay || userLogin} zostało pomyślnie zmienione!`, 'success');
                }
            } else {
                const errText = await response.text();
                if (onNotify) {
                    onNotify('Błąd podczas zmiany hasła: ' + (errText || response.statusText), 'error');
                }
            }
        } catch (error) {
            console.error('Błąd zmiany hasła:', error);
            if (onNotify) {
                onNotify('Wystąpił błąd podczas zmiany hasła.', 'error');
            }
        }
    };

    const copyCredentialsToClipboard = () => {
        if (!createdCredentials) return;
        const text = `System Pelikan - Dane logowania:\nLogin: ${createdCredentials.username}\nHasło: ${createdCredentials.password}`;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>
                    Użytkownicy i Uprawnienia
                </Typography>
                <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                    Zarządzaj kontami użytkowników, przydzielaj role systemowe oraz twórz nowe profile dostępowe.
                </Typography>
            </Box>

            {/* Segmented Sub-Navigation */}
            <Box
                sx={{
                    display: 'inline-flex',
                    p: '4px',
                    borderRadius: '10px',
                    backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.05)' : '#F1F5F9',
                    mb: 3,
                    border: '1px solid',
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    gap: 0.5,
                    flexWrap: 'wrap'
                }}
            >
                <Button
                    onClick={() => { setSubTab('list'); setErrors({}); }}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: subTab === 'list'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: subTab === 'list'
                            ? (darkTheme ? '#6366F1' : '#4F46E5')
                            : 'transparent',
                        boxShadow: subTab === 'list' ? '0 2px 6px rgba(99, 102, 241, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: subTab === 'list'
                                ? (darkTheme ? '#4F46E5' : '#4338CA')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-users" style={{ marginRight: 8 }}></i>
                    Lista użytkowników
                </Button>
                <Button
                    onClick={() => { setSubTab('editRole'); setErrors({}); }}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: subTab === 'editRole'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: subTab === 'editRole'
                            ? (darkTheme ? '#2563EB' : '#1D4ED8')
                            : 'transparent',
                        boxShadow: subTab === 'editRole' ? '0 2px 6px rgba(37, 99, 235, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: subTab === 'editRole'
                                ? (darkTheme ? '#1D4ED8' : '#1E40AF')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-user-shield" style={{ marginRight: 8 }}></i>
                    Zmień uprawnienia
                </Button>
                <Button
                    onClick={() => { setSubTab('addUser'); setErrors({}); setPasswordChangeSuccess(null); }}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: subTab === 'addUser'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: subTab === 'addUser'
                            ? (darkTheme ? '#10B981' : '#059669')
                            : 'transparent',
                        boxShadow: subTab === 'addUser' ? '0 2px 6px rgba(16, 185, 129, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: subTab === 'addUser'
                                ? (darkTheme ? '#059669' : '#047857')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-user-plus" style={{ marginRight: 8 }}></i>
                    Dodaj użytkownika
                </Button>
                <Button
                    onClick={() => { setSubTab('changePassword'); setErrors({}); setPasswordChangeSuccess(null); }}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: subTab === 'changePassword'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: subTab === 'changePassword'
                            ? (darkTheme ? '#D97706' : '#B45309')
                            : 'transparent',
                        boxShadow: subTab === 'changePassword' ? '0 2px 6px rgba(217, 119, 6, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: subTab === 'changePassword'
                                ? (darkTheme ? '#B45309' : '#92400E')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-key" style={{ marginRight: 8 }}></i>
                    Zmień hasło
                </Button>
            </Box>

            {/* Karta informacyjna o nowo utworzonym użytkowniku */}
            {createdCredentials && (
                <Alert
                    severity="success"
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        alignItems: 'center',
                        backgroundColor: darkTheme ? 'rgba(16, 185, 129, 0.12)' : undefined,
                        border: '1px solid',
                        borderColor: darkTheme ? 'rgba(16, 185, 129, 0.3)' : '#A7F3D0'
                    }}
                    action={
                        <Button
                            color="success"
                            size="small"
                            onClick={copyCredentialsToClipboard}
                            startIcon={<ContentCopyIcon />}
                            sx={{ fontWeight: 600 }}
                        >
                            {copied ? 'Skopiowano!' : 'Kopiuj dane'}
                        </Button>
                    }
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Nowy użytkownik został pomyślnie utworzony!
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Login: <strong>{createdCredentials.username}</strong> | Hasło: <strong>{createdCredentials.password}</strong>
                    </Typography>
                </Alert>
            )}

            {/* WIDOK: Lista użytkowników */}
            {subTab === 'list' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#FFFFFF'
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkTheme ? '#F1F5F9' : '#0F172A' }}>
                                    Lista użytkowników z przypisanymi rolami
                                </Typography>
                                <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                                    Łącznie zarejestrowanych kont: <strong>{users.length}</strong>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                                <TextField
                                    size="small"
                                    placeholder="Filtruj (nazwisko, login, rola)..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <i className="fas fa-search" style={{ color: darkTheme ? '#94A3B8' : '#64748B', fontSize: 13 }}></i>
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSearchTerm('');
                                                        if (gridRef.current?.api) {
                                                            gridRef.current.api.setQuickFilter('');
                                                        }
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <i className="fas fa-times" style={{ fontSize: 11, color: darkTheme ? '#94A3B8' : '#64748B' }}></i>
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null
                                    }}
                                    sx={{
                                        width: { xs: '100%', sm: 280 },
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.04)' : '#F8FAFC'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        <div
                            className={darkTheme ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
                            style={{ height: 480, width: '100%', borderRadius: 8, overflow: 'hidden' }}
                        >
                            <AgGridReact
                                key={darkTheme ? 'grid-dark' : 'grid-light'}
                                rowData={users}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                ref={gridRef}
                                onGridReady={onGridReady}
                                animateRows={true}
                                headerHeight={42}
                                rowHeight={48}
                                popupParent={document.body}
                                localeText={MyCollections.localeTextPL}
                                overlayNoRowsTemplate={'<div class="ag-overlay-loading-center" style="font-weight: 500;">Brak użytkowników do wyświetlenia</div>'}
                            />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* WIDOK: Zmiana uprawnień */}
            {subTab === 'editRole' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#FFFFFF',
                        maxWidth: 720
                    }}
                >
                    <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkTheme ? '#F1F5F9' : '#0F172A', mb: 2 }}>
                            Edycja roli użytkownika
                        </Typography>

                        <Grid container spacing={2.5}>
                            <Grid item xs={12}>
                                <Controls.Select
                                    name="id"
                                    label="Wybierz użytkownika"
                                    value={selectedUser}
                                    onChange={handleUserChange}
                                    options={users}
                                    error={errors.id}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controls.Select
                                    name="role"
                                    label="Przypisz rolę w systemie"
                                    value={role}
                                    onChange={handleRoleChange}
                                    options={MyCollections.getRoleCollection()}
                                    error={errors.role}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 1 }}>
                                <Controls.PremiumButton
                                    variant="green"
                                    label="Zapisz uprawnienia"
                                    icon="fas fa-save"
                                    onClick={handleSubmitRoleChange}
                                    disabled={!selectedUser || !role}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* WIDOK: Zmiana hasła */}
            {subTab === 'changePassword' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#FFFFFF',
                        maxWidth: 720
                    }}
                >
                    <CardContent sx={{ p: 3.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkTheme ? '#F1F5F9' : '#0F172A' }}>
                                    Zmiana hasła użytkownika
                                </Typography>
                                <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                                    Wprowadź nowe hasło dla wybranego konta lub wygeneruj bezpieczne hasło
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="text"
                                onClick={generateRandomPassword}
                                startIcon={<i className="fas fa-magic" style={{ fontSize: 12 }}></i>}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '12.5px',
                                    color: darkTheme ? '#FCD34D' : '#D97706'
                                }}
                            >
                                Wygeneruj bezpieczne hasło
                            </Button>
                        </Box>

                        {passwordChangeSuccess && (
                            <Alert
                                severity="success"
                                sx={{
                                    mb: 3,
                                    borderRadius: 2,
                                    alignItems: 'center',
                                    backgroundColor: darkTheme ? 'rgba(16, 185, 129, 0.12)' : undefined,
                                    border: '1px solid',
                                    borderColor: darkTheme ? 'rgba(16, 185, 129, 0.3)' : '#A7F3D0'
                                }}
                                action={
                                    <Button
                                        color="success"
                                        size="small"
                                        onClick={() => {
                                            navigator.clipboard.writeText(passwordChangeSuccess.password);
                                            setCopiedNewPassword(true);
                                            setTimeout(() => setCopiedNewPassword(false), 3000);
                                        }}
                                        startIcon={<ContentCopyIcon />}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {copiedNewPassword ? 'Skopiowano!' : 'Kopiuj nowe hasło'}
                                    </Button>
                                }
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Hasło dla użytkownika {passwordChangeSuccess.displayName || passwordChangeSuccess.username} zostało pomyślnie zmienione!
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Nowe hasło: <strong>{passwordChangeSuccess.password}</strong>
                                </Typography>
                            </Alert>
                        )}

                        <Box component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12}>
                                    <Controls.Select
                                        name="passwordUser"
                                        label="Wybierz użytkownika"
                                        value={selectedUser}
                                        onChange={(e) => {
                                            setSelectedUser(e.target.value);
                                            setPasswordChangeSuccess(null);
                                        }}
                                        options={users}
                                        error={errors.passwordUser}
                                    />
                                </Grid>
                                {selectedUser && (
                                    <Grid item xs={12}>
                                        {(() => {
                                            const u = users.find(x => (x.id || x.key) === selectedUser);
                                            if (!u) return null;
                                            return (
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC',
                                                    border: '1px solid',
                                                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.06)' : '#E2E8F0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                                                        Login: <strong style={{ color: darkTheme ? '#F1F5F9' : '#0F172A', fontFamily: 'monospace' }}>{u.username || '—'}</strong>
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                                                        Aktualna rola: <strong style={{ color: darkTheme ? '#F1F5F9' : '#0F172A' }}>{roleTranslations[u.role || u.userRole] || u.role || '—'}</strong>
                                                    </Typography>
                                                </Box>
                                            );
                                        })()}
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="newPassword"
                                        label="Nowe hasło"
                                        type={showNewPassword ? 'text' : 'password'}
                                        variant="outlined"
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value);
                                            if (errors.newPassword) {
                                                setErrors(prev => {
                                                    const next = { ...prev };
                                                    delete next.newPassword;
                                                    return next;
                                                });
                                            }
                                        }}
                                        fullWidth
                                        error={!!errors.newPassword}
                                        helperText={errors.newPassword || 'Min. 12 znaków, duża i mała litera, cyfra, znak specjalny'}
                                        autoComplete="new-password"
                                        inputProps={{
                                            autoComplete: 'new-password',
                                            'data-lpignore': 'true',
                                            'data-1p-ignore': 'true'
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="confirmPassword"
                                        label="Powtórz nowe hasło"
                                        type={showNewPassword ? 'text' : 'password'}
                                        variant="outlined"
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value);
                                            if (errors.confirmPassword) {
                                                setErrors(prev => {
                                                    const next = { ...prev };
                                                    delete next.confirmPassword;
                                                    return next;
                                                });
                                            }
                                        }}
                                        fullWidth
                                        error={!!errors.confirmPassword}
                                        helperText={errors.confirmPassword}
                                        autoComplete="new-password"
                                        inputProps={{
                                            autoComplete: 'new-password',
                                            'data-lpignore': 'true',
                                            'data-1p-ignore': 'true'
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ pt: 1, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                    <Controls.PremiumButton
                                        variant="green"
                                        label="Zapisz nowe hasło"
                                        icon="fas fa-key"
                                        onClick={handleSubmitPasswordChange}
                                        disabled={!selectedUser || !newPassword || !confirmPassword}
                                    />
                                    <Button
                                        variant="text"
                                        onClick={() => { setSubTab('list'); setErrors({}); }}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            color: darkTheme ? '#94A3B8' : '#64748B'
                                        }}
                                    >
                                        Wróć do listy
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* WIDOK: Dodawanie użytkownika */}
            {subTab === 'addUser' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#FFFFFF',
                        maxWidth: 760
                    }}
                >
                    <CardContent sx={{ p: 3.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkTheme ? '#F1F5F9' : '#0F172A' }}>
                                    Formularz nowego użytkownika
                                </Typography>
                                <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                                    Wprowadź dane nowego konta i hasło lub skorzystaj z generatora bezpiecznego hasła
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="text"
                                onClick={generateRandomPasswordForNewUser}
                                startIcon={<i className="fas fa-magic" style={{ fontSize: 12 }}></i>}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '12.5px',
                                    color: darkTheme ? '#34D399' : '#059669'
                                }}
                            >
                                Wygeneruj bezpieczne hasło
                            </Button>
                        </Box>

                        <Box component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="firstName"
                                        label="Imię"
                                        placeholder="np. Jan"
                                        variant="outlined"
                                        value={formData.firstName || ''}
                                        onChange={localOnChange}
                                        fullWidth
                                        error={!!errors.firstName}
                                        helperText={errors.firstName}
                                        autoComplete="off"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="lastName"
                                        label="Nazwisko"
                                        placeholder="np. Kowalski"
                                        variant="outlined"
                                        value={formData.lastName || ''}
                                        onChange={localOnChange}
                                        fullWidth
                                        error={!!errors.lastName}
                                        helperText={errors.lastName}
                                        autoComplete="off"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="username"
                                        label="Nazwa użytkownika (login)"
                                        placeholder="np. jkowalski"
                                        variant="outlined"
                                        value={formData.username || ''}
                                        onChange={localOnChange}
                                        fullWidth
                                        error={!!errors.username}
                                        helperText={errors.username}
                                        autoComplete="off"
                                        inputProps={{
                                            autoComplete: 'off',
                                            autoCorrect: 'off',
                                            spellCheck: 'false',
                                            'data-lpignore': 'true',
                                            'data-1p-ignore': 'true'
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="password"
                                        label="Hasło"
                                        type={showPassword ? 'text' : 'password'}
                                        variant="outlined"
                                        value={formData.password || ''}
                                        onChange={localOnChange}
                                        fullWidth
                                        error={!!errors.password}
                                        helperText={errors.password || 'Min. 12 znaków, duża i mała litera, cyfra, znak specjalny'}
                                        autoComplete="new-password"
                                        inputProps={{
                                            autoComplete: 'new-password',
                                            'data-lpignore': 'true',
                                            'data-1p-ignore': 'true'
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={generateRandomPasswordForNewUser}
                                                        title="Wygeneruj bezpieczne hasło"
                                                        size="small"
                                                        sx={{ mr: 0.5, color: darkTheme ? '#34D399' : '#059669' }}
                                                    >
                                                        <i className="fas fa-magic" style={{ fontSize: 13 }}></i>
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={formData.userRole === 'ACCOUNTANT' ? 6 : 12}>
                                    <Controls.Select
                                        name="userRole"
                                        label="Rola w systemie"
                                        value={formData.userRole || ''}
                                        onChange={localOnChange}
                                        options={MyCollections.getRoleCollection()}
                                        error={errors.userRole}
                                    />
                                </Grid>
                                {formData.userRole === 'ACCOUNTANT' && (
                                    <Grid item xs={12} sm={6}>
                                        <Controls.Select
                                            name="contactId"
                                            label="Przypisz profil księgowego"
                                            value={formData.contactId || ''}
                                            onChange={localOnChange}
                                            options={accountants}
                                            error={errors.contactId}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} sx={{ pt: 1.5 }}>
                                    <Controls.PremiumButton
                                        variant="green"
                                        label="Utwórz konto użytkownika"
                                        icon="fas fa-user-plus"
                                        onClick={handleAddUserSubmit}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Dialog potwierdzenia resetu 2FA dla użytkownika */}
            <Dialog
                open={reset2FaConfirmOpen}
                onClose={() => setReset2FaConfirmOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '14px',
                        backgroundColor: darkTheme ? '#1E293B' : '#FFFFFF',
                        color: darkTheme ? '#F8FAFC' : '#0F172A',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Resetuj 2FA dla użytkownika</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569' }}>
                        Czy na pewno chcesz zresetować weryfikację dwuskładnikową (2FA) dla użytkownika <b>{reset2FaTarget?.name}</b>?
                        Użytkownik odzyska możliwość logowania samym loginem i hasłem, a dotychczasowe kody zapasowe i klucz w aplikacji zostaną unieważnione.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setReset2FaConfirmOpen(false)} sx={{ textTransform: 'none' }}>
                        Anuluj
                    </Button>
                    <Button onClick={handleExecuteReset2Fa} color="error" variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Zresetuj 2FA
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
